import { Container } from '@pixi/react'
import { memo, useEffect, useMemo, useRef } from 'react'
import { Container as PixiContainer } from 'pixi.js'
import { appScreen, FixedContainerV2, Image, Loading, soundManager } from '@slot-nx/core'
import { spinService } from '../domain/game'
import { Symbol } from './Symbol'
import { gameConfig } from '../config'
import { useGameStore } from '../domain/game/store/GameStore'
import { changeNode } from '../domain/game/util/gameUtil'
import gsap from 'gsap'
import { useSelector } from '@xstate/react'
import { GameState, MachineActionType, sweetBombMachine } from '../../machine/sweetbombMachine'
import { SweetBombSoundType } from '../domain/soundManager/enum/soundType'
import { SlotSymbolInfo } from '../domain/placeOrder/api/PlaceOrderResponse'
const reelNum = 6
const initX = 0
const reelGap = 165

const width = 990
const height = 825

export const Slot = memo(() => {
  const currentState = useSelector(sweetBombMachine, state => state)
  const x = (appScreen.width - width) / 2
  const y = 375

  const reelTableRef = useRef<PixiContainer>()
  const slotTableRef = useRef<PixiContainer>()
  const reelRefs = useRef<PixiContainer[]>([])
  const { defaultReelTable, isAnteBet } = useGameStore()
  const { isFreeGame, isShowPlaceOrderLoadingIcon } = useGameStore()

  const changeReelTable = useMemo(() => {
    if (isFreeGame) {
      return 'freeGameTable'
    }
    else {
      return 'mainGametable'
    }
  }, [isFreeGame])

  const Reels = useMemo(() => {
    return Array.from({ length: reelNum }, (_, i) => {
      return (
        defaultReelTable.length > 0 && (
          <Container
            name={`Reel ${i}`}
            x={initX + i * reelGap}
            y={0}
            width={0}
            height={0}
            key={i}
            ref={(el) => {
              reelRefs.current[i] = el as PixiContainer
            }}
          >
            {defaultReelTable[i].map((symbol: SlotSymbolInfo, j) => (
              <Symbol key={j} x={0} y={(defaultReelTable[i].length - 1 - j) * gameConfig.nodeHeight} data={symbol} />
            ))}
          </Container>
        )
      )
    })
  }, [defaultReelTable])

  useEffect(() => {
    const reels = reelRefs.current.map(x => x as PixiContainer)
    spinService.initReels(reels, slotTableRef.current as PixiContainer)
  }, [])

  useEffect(() => {
    const reelTable = reelRefs.current
    if (defaultReelTable.length > 0) {
      reelTable.forEach((reel, reelIndex) => {
        reel.children.forEach((symbol, symbolIndex) => {
          changeNode(symbol as PixiContainer, defaultReelTable[reelIndex][symbolIndex])
        })
      })
    }
  }, [defaultReelTable, reelRefs])

  useEffect(() => {
    const node = reelTableRef.current
    if (isAnteBet && node) {
      soundManager.play(SweetBombSoundType.Boost)
      useGameStore.setState({
        isShakeing: true,
      })
      const duration = 0.05
      const shake = gsap.timeline({
        onComplete: () => {
          useGameStore.setState({
            isShakeing: false,
          })
        } })

      shake.to(node, {
        x: -10,
        y: -20,
        rotate: -10,
        yoyo: true,
        duration,
      })
      shake.to(node, {
        x: -30,
        y: 0,
        rotate: 10,
        yoyo: true,
        duration,
      })
      shake.to(node, {
        x: 30,
        y: 20,
        rotate: 0,
        yoyo: true,
        duration,
      })
      shake.to(node, {
        x: 10,
        y: -10,
        rotate: 10,
        yoyo: true,
        duration,
      })
      shake.to(node, {
        x: -10,
        y: 20,
        rotate: -10,
        yoyo: true,
        duration,
      })
      shake.to(node, {
        x: -30,
        y: 10,
        rotate: 0,
        yoyo: true,
        duration,
      })
      shake.to(node, {
        x: 30,
        y: 10,
        rotate: -10,
        yoyo: true,
        duration,
      })
      shake.to(node, {
        x: -10,
        y: -10,
        rotate: 10,
        yoyo: true,
        duration,
      })
      shake.to(node, {
        x: 10,
        y: 20,
        rotate: 0,
        yoyo: true,
        duration,
      })
      shake.to(node, {
        x: 10,
        y: -20,
        rotate: -10,
        yoyo: true,
        duration,
      })
      shake.to(node, {
        x: 0,
        y: 0,
        yoyo: false,
        duration,
      })
    }
  }, [isAnteBet])

  const spin = () => {
    if (currentState.matches(GameState.mainGame)) {
      sweetBombMachine.send({ type: MachineActionType.placeOrder })
    }
    if (currentState.matches(GameState.displayGameResult)) {
      spinService.emergencyStop()
    }
  }

  return (
    <Container
      ref={(el) => {
        reelTableRef.current = el as PixiContainer
      }}
      eventMode="static"
      onpointertap={() => {
        spin()
      }}
    >
      <Image name="tableImg" image={changeReelTable} width={1071} height={881} x={5} y={350} />
      <FixedContainerV2
        name="Slot"
        width={width}
        height={height}
        position={[x, y]}
        ref={(el) => {
          slotTableRef.current = el as PixiContainer
        }}
      >
        {Reels}
      </FixedContainerV2>
      { isShowPlaceOrderLoadingIcon
        ? <Loading x={gameConfig.resolution.width / 2} y={1400 / 2} width={120} height={120} />
        : null}
    </Container>
  )
})
